import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import VideoEmbed from './VideoEmbed';
import './VideoTutorialModal.css';

/**
 * Modal moderne pour les vidéos tutoriels avec design glassmorphique
 * Supporte la playlist, les contrôles personnalisés et l'responsive
 */
const VideoTutorialModal = ({ 
  isOpen, 
  onClose, 
  video, 
  allVideos = [], 
  onVideoSelect 
}) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoSize, setVideoSize] = useState('normal'); // 'small', 'normal', 'large', 'xlarge', 'fullscreen'
  const videoRef = useRef(null);
  const playerContainerRef = useRef(null);

  useEffect(() => {
    if (isOpen && videoRef.current) {
      videoRef.current.load();
      setIsPlaying(false);
      setCurrentTime(0);
    }
  }, [isOpen, video]);

  const videoSizes = {
    normal: { width: '854px', height: '480px', label: 'Normal' },
    large: { width: '1280px', height: '720px', label: 'Grand' }
  };

  const toggleVideoSize = () => {
    const sizes = Object.keys(videoSizes);
    const currentIndex = sizes.indexOf(videoSize);
    const nextIndex = (currentIndex + 1) % sizes.length;
    setVideoSize(sizes[nextIndex]);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case ' ':
          e.preventDefault();
          togglePlayPause();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, videoSize]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoSelect = (selectedVideo) => {
    onVideoSelect(selectedVideo.id);
    setShowPlaylist(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen || !video) return null;

  return (
    <div className="video-tutorial-overlay" onClick={onClose}>
      <div className="video-tutorial-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="video-tutorial-header">
          <div className="video-tutorial-info">
            <h2 className="video-tutorial-title">{video.title}</h2>
            <div className="video-tutorial-meta">
              <span className="video-duration">🕐 {video.duration}</span>
              <span className="video-views">👁️ {t('tutorials.educational', 'Éducatif')}</span>
            </div>
          </div>
          
          <div className="video-tutorial-actions">
            {allVideos.length > 1 && (
              <button
                onClick={() => setShowPlaylist(!showPlaylist)}
                className={`playlist-toggle-btn ${showPlaylist ? 'active' : ''}`}
                title={t('tutorials.togglePlaylist', 'Afficher/Masquer la playlist')}
              >
                📋
              </button>
            )}
            
            <button
              onClick={onClose}
              className="video-tutorial-close"
              title={t('common.close', 'Fermer')}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="video-tutorial-content">
          {/* Lecteur vidéo */}
          <div
            ref={playerContainerRef}
            className={`video-tutorial-player ${videoSize}`}
            style={{
              width: videoSizes[videoSize].width,
              height: videoSizes[videoSize].height,
              maxWidth: videoSize === 'large' ? 'none' : '90vw',
              maxHeight: videoSize === 'large' ? 'none' : '80vh'
            }}
          >
            <VideoEmbed
              videoId={
                video.action === 'createNode'
                  ? 'lfR4rK9ggLo'
                  : video.action === 'createTest'
                  ? 'q1p6c5yjsuE'
                  : video.videoId
              }
              style={{ width: '100%', height: '100%' }}
            />
          </div>

          {/* Playlist (conditionnelle) - Masquée en plein écran */}
          {showPlaylist && allVideos.length > 1 && (
            <div className="video-tutorial-playlist">
              <h3 className="playlist-title">
                📚 {t('tutorials.playlist', 'Autres Tutoriels')}
              </h3>
              <div className="playlist-items">
                {allVideos.map((playlistVideo) => (
                  <div
                    key={playlistVideo.id}
                    onClick={() => handleVideoSelect(playlistVideo)}
                    className={`playlist-item ${video.id === playlistVideo.id ? 'active' : ''}`}
                  >
                    <div className="playlist-thumbnail">
                      <img src={playlistVideo.thumbnail} alt={playlistVideo.title} />
                      <div className="playlist-duration">{playlistVideo.duration}</div>
                    </div>
                    <div className="playlist-info">
                      <h4>{playlistVideo.title}</h4>
                      <p>{playlistVideo.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Description - Masquée en plein écran */}
        {videoSize !== 'fullscreen' && (
          <div className="video-tutorial-description">
            <h3>{t('tutorials.about', 'À propos de ce tutoriel')}</h3>
            <p>{video.description}</p>
            {/* If this is the Quick Create tutorial, show the short "what is a node" explanatory text */}
            {video.id === 'quickCreate' && (
              <div className="what-is-node-container">
              <p className="what-is-node">{t('tutorials.quickCreate.whatIsNode')}</p>
              </div>
            )}

            {/* If this is the Relations tutorial, show the short "what is a relation" explanatory text */}
            {video.id === 'relations' && (
              <div className="what-is-node-container">
                <p className="what-is-node">{t('tutorials.relations.whatIsRelation')}</p>
              </div>
            )}

            {/* If this is the AI Test Generation tutorial, show a short explanatory text */}
            {video.id === 'tests' && (
              <div className="what-is-node-container">
                <p className="what-is-node">{t('tutorials.tests.whatIsTest')}</p>
              </div>
            )}

            <div className="tutorial-tags">
              <span className="tutorial-tag">🎯 {t('tutorials.tag.beginner', 'Débutant')}</span>
              <span className="tutorial-tag">⚡ {t('tutorials.tag.quickStart', 'Démarrage rapide')}</span>
              <span className="tutorial-tag">🔧 {t('tutorials.tag.practical', 'Pratique')}</span>
            </div>

         
          </div>
        )}

        {/* Footer avec actions - Masqué en plein écran */}
        {videoSize !== 'fullscreen' && (
          <div className="video-tutorial-footer">
            <div className="tutorial-actions">
              <button className="tutorial-action-btn secondary">
                👍 {t('tutorials.helpful', 'Utile')}
              </button>
              <button className="tutorial-action-btn secondary">
                💾 {t('tutorials.bookmark', 'Sauvegarder')}
              </button>
              <button className="tutorial-action-btn secondary">
                📤 {t('tutorials.share', 'Partager')}
              </button>
            </div>
            
            <button 
              onClick={onClose}
              className="tutorial-action-btn primary"
            >
              {t('tutorials.startPracticing', 'Commencer à pratiquer')} →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoTutorialModal;